import { useTranslatedGridLocaleText } from '@/hooks/useTranslatedGridLocaleText';
import {
  ColDef,
  ColumnEvent,
  ColumnState,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideGetRowsParams,
  SideBarDef,
  SortChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getAuth } from '../../../selectors/auth';
import './Grid.scss';
import Pagination from './Pagination';
import { RestoreDefaultStatusBarComponent } from './RestoreDefaultsStatusBarComponent';

const gridIcons = {
  sortAscending: '<i class="icon fas fa-caret-up"></i>',
  sortDescending: '<i class="icon fas fa-caret-down"></i>',
  filterActive: '<i class="icon icon-xs fas fa-filter"></i>',
};

export const baseGridOptions: GridOptions = {
  headerHeight: 30,
  cellSelection: true,
  rowSelection: {
    mode: 'multiRow',
    copySelectedRows: false,
    headerCheckbox: false,
    checkboxes: false,
  },
};

interface GridProp {
  gridOptions?: GridOptions;
  columnDefs: any[];
  rowData?: any;
  modules?: any[];
  getRowNodeId?: (data: any) => any;
  immutableData?: boolean;
  loading?: boolean;
  disableGroupCollapse?: boolean;
  onRowDoubleClicked?: (event: any) => void;
  setGridApi?: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
  onGridReady?: (params: GridReadyEvent) => void;
  resetColumnsProvider?: (any: any) => any;
  gridId?: string;
  persistPagination?: boolean;
  rowMultiSelectWithClick?: boolean;
  onFilterModelUpdate?: (model: any) => any;
  onResetFilters?: () => void;
  isExternalFilterPresent?: () => boolean;
  doesExternalFilterPass?: (node: any) => boolean;
  paginationAutoPageSize?: boolean;
  pagination?: boolean;
  paginationPageSize?: number;
  suppressRowClickSelection?: boolean;
  suppressVirtualisation?: boolean;
  getServerSideRows?: (params: IServerSideGetRowsParams) => void;
  onSortUpdated?: (columnState: ColumnState[]) => void;
  getRowId?: (data: any) => any;
  rowModelType?: 'serverSide' | 'clientSide';
  externalFiltersUpdated?: boolean;
  setExternalFiltersUpdated?: (arg0: boolean) => void;
  context: any;
  refreshGrid: boolean;
  name: string;
}

export default function Grid(props: GridProp) {
  const {
    gridOptions,
    columnDefs,
    suppressRowClickSelection,
    suppressVirtualisation,
    name,
    getServerSideRows,
    externalFiltersUpdated,
    setExternalFiltersUpdated,
    onSortUpdated,
    onResetFilters,
    refreshGrid,
    ...rest
  } = props;

  const gridRef = useRef<AgGridReact>(null);
  const { t } = useTranslation();
  const { getColumnState, setColumnState } = useColumnState(name);
  const { getLocaleText } = useTranslatedGridLocaleText();

  useEffect(() => {
    gridRef.current?.api?.refreshServerSide();
  }, [refreshGrid, gridRef]);

  useEffect(() => {
    if (
      gridRef.current &&
      setExternalFiltersUpdated &&
      externalFiltersUpdated &&
      getServerSideRows
    ) {
      gridRef.current.api.setGridOption('serverSideDatasource', {
        getRows: getServerSideRows,
      });
      setExternalFiltersUpdated(false);
    }
  }, [externalFiltersUpdated]);

  const onGridReadySetGridApi = (params: GridReadyEvent) => {
    if (getServerSideRows) {
      params.api.setGridOption('serverSideDatasource', {
        getRows: getServerSideRows,
      });
    }
  };

  const finalColumnDefs = useMemo(
    () =>
      columnDefs.map((ob) => ({
        ...(typeof ob === 'object' ? ob : {}),
      })),
    [columnDefs]
  );

  const finalGridOptions: GridOptions = useMemo(
    () => ({
      getLocaleText,
      ...baseGridOptions,
      ...gridOptions,
      maxBlocksInCache: 1,
      theme: 'legacy',
    }),
    [getLocaleText, gridOptions]
  );

  const onSortChanged = (event: SortChangedEvent) => {
    if (onSortUpdated) onSortUpdated(event.api.getColumnState());
    updateColumnState();
  };
  function getRowNodeId(data: any) {
    return String(data.data.id);
  }

  const updateColumnState = () => {};
  const updatePaginationChanged = () => {};
  const paginationPageSize = 50;
  const pagination = props.pagination ?? true;

  const components: any = useMemo(
    () =>
      pagination
        ? {
            Pagination,
          }
        : null,
    [pagination]
  );

  const statusBar: GridOptions['statusBar'] = useMemo(
    () =>
      pagination
        ? {
            statusPanels: [
              {
                statusPanel: 'Pagination',
                align: 'center',
              },
              {
                statusPanel: RestoreDefaultStatusBarComponent,
                statusPanelParams: { t, onResetFilters },
              },
            ],
          }
        : {
            statusPanels: [
              {
                statusPanel: RestoreDefaultStatusBarComponent,
                statusPanelParams: { t, onResetFilters },
              },
            ],
          },
    [onResetFilters, pagination, t]
  );

  const onColumnStateUpdated = debounce((event: ColumnEvent) => {
    if (event.source === 'flex') return;
    const columnState = event.api.getColumnState();
    setColumnState(columnState);
  }, 100);

  const onDataFirstRendered = (event: FirstDataRenderedEvent<any>) => {
    const columnState = getColumnState();
    if (columnState) {
      event.api.applyColumnState({
        state: columnState,
        applyOrder: true,
      });
    }
  };

  const sideBar: SideBarDef = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressValues: true,
            suppressRowGroups: true,
            suppressColumnFilter: true,
          },
        },
      ],
    }),
    []
  );

  const defaultColDef: ColDef<any, any> = useMemo(
    () => ({
      cellStyle: () => ({
        display: 'flex',
        alignItems: 'left',
        justifyContent: 'left',
      }),
      menuTabs: ['filterMenuTab'],
      suppressHeaderMenuButton: true,
    }),
    []
  );

  return (
    <div className="app-grid-container ag-theme-alpine">
      <AgGridReact
        ref={gridRef}
        gridOptions={finalGridOptions}
        columnDefs={finalColumnDefs}
        pagination={true}
        paginationPageSize={paginationPageSize}
        suppressPaginationPanel={pagination}
        rowModelType={'serverSide'}
        cacheBlockSize={paginationPageSize}
        onGridReady={onGridReadySetGridApi}
        onFilterChanged={updateColumnState}
        onSortChanged={onSortChanged}
        multiSortKey={'ctrl'}
        onColumnVisible={onColumnStateUpdated}
        onColumnPinned={onColumnStateUpdated}
        onColumnResized={onColumnStateUpdated}
        onColumnRowGroupChanged={onColumnStateUpdated}
        onColumnValueChanged={onColumnStateUpdated}
        onColumnPivotChanged={onColumnStateUpdated}
        onColumnMoved={onColumnStateUpdated}
        sideBar={sideBar}
        suppressExpandablePivotGroups={true}
        onPaginationChanged={updatePaginationChanged}
        onFirstDataRendered={onDataFirstRendered}
        suppressRowClickSelection={suppressRowClickSelection}
        suppressRowVirtualisation={suppressVirtualisation}
        suppressColumnVirtualisation={suppressVirtualisation}
        components={components}
        suppressMenuHide={true}
        icons={gridIcons}
        getRowId={getRowNodeId}
        statusBar={statusBar}
        maintainColumnOrder={true}
        columnMenu="legacy"
        defaultColDef={defaultColDef}
        {...rest}
      ></AgGridReact>
    </div>
  );
}

function useColumnState(name: string) {
  const { sub } = useSelector(getAuth);
  const key = `COLUMN_STATE_${name.toUpperCase()}_${sub.toUpperCase()}`;

  const getColumnState = () => {
    const item = localStorage.getItem(key);
    if (item) return JSON.parse(item) as ColumnState[];
  };

  const setColumnState = (columnState: ColumnState[]) => {
    localStorage.setItem(key, JSON.stringify(columnState));
  };

  return { getColumnState, setColumnState };
}
